/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */
'use strict';

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Col, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { StatusContext } from './StatusContext';
import { WsContext } from './WsContext';
import { MetadataContext } from './MetadataContext';
import {
    exportGit, importGit,
    gitLog, gitNextSerial, gitCommit, gitCommitWithAuthor, gitLoadCommit,
    getGitPublishState, pushGitToS3,
    runFbotBuildAction,
    terraformListSiteNames,
    getBundleWorkflowInfo,
    mergeImportQueue, mergeLoadFile,
    getMetadata, setMetadata, updateProgressBatched, publishProgressToS3,
    getDiff,
    BundleWorkflowInfo,
    FbotBuildAction, FbotBuildCommonOptions,
    GitCommitEntry, GitPublishStateResult, PushGitToS3Result,
    MergeQueueEntry,
} from '../data';
import CompareTab from './CompareTab';

const DEFAULT_COMMIT_AUTHOR_NAME  = 'FBot';
const DEFAULT_COMMIT_AUTHOR_EMAIL = 'fbot@ipivot.ai';

function safePrefix(value: any, len: number): string {
    return String(value || '').slice(0, len);
}

function safeDateYmd(value: any): string {
    const txt = String(value || '');
    return txt.length >= 10 ? txt.slice(0, 10) : txt;
}

const TABS = ['history', 'compare', 'commit', 'sync', 'publish', 'fbot-build', 'fingerprint-workflow'] as const;
type Tab = typeof TABS[number];

// ---------------------------------------------------------------------------
// Design tokens (shared with OpsPage)
// ---------------------------------------------------------------------------

const T = {
    bg:          '#111318',
    card:        '#171A21',
    cardEl:      '#1D212B',
    rail:        '#0C0F15',
    border:      '#2A3140',
    textPrimary: '#E6EAF2',
    textMuted:   '#98A2B3',
    textFaint:   '#5A6478',
    success:     '#22C55E',
    warning:     '#F59E0B',
    danger:      '#EF4444',
    action:      '#60A5FA',
} as const;

// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------

const inputStyle: React.CSSProperties = {
    background: T.cardEl,
    border: `1px solid ${T.border}`,
    borderRadius: 5,
    color: T.textPrimary,
    padding: '6px 10px',
    fontSize: '0.82rem',
    width: '100%',
    outline: 'none',
    fontFamily: 'inherit',
};

function Field({ label, hint, children, style }: {
    label: string; hint?: string; children: React.ReactNode; style?: React.CSSProperties;
}) {
    return (
        <div style={style}>
            <div style={{ color: T.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
            {children}
            {hint && <div style={{ color: T.textFaint, fontSize: '0.75rem', marginTop: 3 }}>{hint}</div>}
        </div>
    );
}

function SectionCard({ title, subtitle, children, action }: {
    title: string; subtitle?: string; children: React.ReactNode; action?: React.ReactNode;
}) {
    return (
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 7, marginBottom: 20 }}>
            <div style={{ padding: '12px 20px 10px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <div style={{ color: T.textPrimary, fontWeight: 600, fontSize: '0.88rem' }}>{title}</div>
                    {subtitle && <div style={{ color: T.textMuted, fontSize: '0.75rem', marginTop: 1 }}>{subtitle}</div>}
                </div>
                {action}
            </div>
            <div style={{ padding: '16px 20px' }}>{children}</div>
        </div>
    );
}

function PageHeader({ title, subtitle, chips }: { title: string; subtitle?: string; chips?: string[] }) {
    return (
        <div style={{ marginBottom: 24 }}>
            <h4 style={{ margin: 0, color: T.textPrimary, fontWeight: 600, fontSize: '1.05rem' }}>{title}</h4>
            {subtitle && <div style={{ color: T.textMuted, fontSize: '0.8rem', marginTop: 4 }}>{subtitle}</div>}
            {chips && chips.length > 0 && (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    {chips.map(c => (
                        <span key={c} style={{ background: T.cardEl, border: `1px solid ${T.border}`, borderRadius: 4, padding: '2px 8px', fontSize: '0.75rem', color: T.textMuted, fontFamily: 'monospace' }}>{c}</span>
                    ))}
                </div>
            )}
        </div>
    );
}

function Btn({
    children, onClick, disabled, variant = 'ghost', color = T.action,
}: {
    children: React.ReactNode; onClick?: () => void; disabled?: boolean;
    variant?: 'solid' | 'ghost' | 'danger'; color?: string;
}) {
    const base: React.CSSProperties = {
        border: 'none', borderRadius: 5, fontSize: '0.78rem', fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer', padding: '5px 12px',
        opacity: disabled ? 0.4 : 1, transition: 'opacity 0.15s',
        display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
    };
    if (variant === 'solid')  return <button style={{ ...base, background: color, color: '#fff' }} onClick={onClick} disabled={disabled}>{children}</button>;
    if (variant === 'danger') return <button style={{ ...base, background: 'transparent', color: T.danger, border: `1px solid ${T.danger}44` }} onClick={onClick} disabled={disabled}>{children}</button>;
    return <button style={{ ...base, background: 'transparent', color, border: `1px solid ${color}44` }} onClick={onClick} disabled={disabled}>{children}</button>;
}

function Chip({ label, color }: { label: string; color: string }) {
    return (
        <span style={{
            display: 'inline-block', fontSize: '0.75rem', fontWeight: 600,
            padding: '2px 6px', borderRadius: 3, letterSpacing: '0.03em',
            color, background: `${color}18`, border: `1px solid ${color}44`, whiteSpace: 'nowrap',
        }}>{label}</span>
    );
}

function ErrorBanner({ msg, onClose }: { msg: string; onClose: () => void }) {
    return (
        <div style={{ background: `${T.danger}18`, border: `1px solid ${T.danger}44`, borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: '0.8rem', color: T.danger, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', color: 'inherit' }}>{msg}</pre>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', padding: 0, flexShrink: 0 }}>✕</button>
        </div>
    );
}

function DataTable({ headers, rows }: { headers: string[]; rows: React.ReactNode[][] }) {
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        {headers.map(h => (
                            <th key={h} style={{ padding: '6px 12px', color: T.textMuted, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', background: T.cardEl, borderBottom: `1px solid ${T.border}`, textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((cells, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${T.border}33` }}>
                            {cells.map((cell, j) => (
                                <td key={j} style={{ padding: '7px 12px', fontSize: '0.8rem', color: T.textPrimary }}>{cell}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function MiniProgressBar({ now, label, animated }: { now: number; label?: string; animated?: boolean }) {
    return (
        <div>
            <div style={{ height: 6, background: T.border, borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                    height: '100%', width: `${Math.min(100, Math.max(0, now))}%`,
                    background: now >= 100 ? T.success : T.action, borderRadius: 3,
                    transition: animated ? 'width 0.3s ease' : 'none',
                }} />
            </div>
            {label && <div style={{ color: T.textMuted, fontSize: '0.75rem', marginTop: 4 }}>{label}</div>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// History tab
// ---------------------------------------------------------------------------

function HistoryTab() {
    const statusContext = useContext(StatusContext);
    const [commits,     setCommits]     = useState<GitCommitEntry[]>([]);
    const [isLoading,   setIsLoading]   = useState(false);
    const [loadingHash, setLoadingHash] = useState<string | null>(null);
    const [error,       setError]       = useState<string | null>(null);

    async function load() {
        setIsLoading(true);
        try { setCommits(await gitLog()); }
        catch (e: any) { setError(e?.message || String(e)); }
        finally { setIsLoading(false); }
    }

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    async function handleLoadCommit(hash: string) {
        setError(null);
        setLoadingHash(hash);
        try {
            await gitLoadCommit(hash);
            statusContext.postInfo(`Loaded database from commit ${safePrefix(hash, 7)}`);
            await load();
        } catch (e: any) {
            const msg = e?.message || String(e);
            setError(msg);
            statusContext.postError(`Load commit failed: ${msg}`);
        } finally { setLoadingHash(null); }
    }

    return (
        <div>
            <PageHeader title="History" subtitle="Git commit history for data/prod.db" />
            {error && <ErrorBanner msg={error} onClose={() => setError(null)} />}
            <SectionCard
                title="Commit History — data/prod.db"
                action={<Btn variant="ghost" color={T.textMuted} onClick={load} disabled={isLoading}>{isLoading ? <Spinner size="sm" /> : 'Refresh'}</Btn>}
            >
                {isLoading && commits.length === 0 ? (
                    <div style={{ color: T.textMuted, padding: '12px 0' }}><Spinner size="sm" className="me-2" />Loading…</div>
                ) : commits.length === 0 ? (
                    <div style={{ color: T.textMuted }}>No commits found for data/prod.db.</div>
                ) : (
                    <DataTable
                        headers={['Hash', 'Date', 'Message', 'Serial', '']}
                        rows={commits.map(c => [
                            <code style={{ color: T.action, fontSize: '0.78rem' }}>{c.shortHash}</code>,
                            <span style={{ color: T.textMuted, whiteSpace: 'nowrap' }}>{safeDateYmd(c.date)}</span>,
                            <span>{c.message}</span>,
                            c.serial ? <Chip label={String(c.serial)} color={T.success} /> : <span style={{ color: T.textFaint }}>—</span>,
                            <Btn variant="ghost" color={T.textMuted} disabled={!!loadingHash} onClick={() => handleLoadCommit(c.hash)}>
                                {loadingHash === c.hash ? <Spinner size="sm" /> : 'Load'}
                            </Btn>,
                        ])}
                    />
                )}
            </SectionCard>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Commit tab
// ---------------------------------------------------------------------------

function CommitTab() {
    const statusContext = useContext(StatusContext);
    const [commitMessage, setCommitMessage] = useState('');
    const [authorName,    setAuthorName]    = useState(DEFAULT_COMMIT_AUTHOR_NAME);
    const [authorEmail,   setAuthorEmail]   = useState(DEFAULT_COMMIT_AUTHOR_EMAIL);
    const [createTag,     setCreateTag]     = useState(false);
    const [serial,        setSerial]        = useState('');
    const [isCommitting,  setIsCommitting]  = useState(false);
    const [error,         setError]         = useState<string | null>(null);

    useEffect(() => {
        gitNextSerial().then(ns => setSerial(String(ns.nextSerial).padStart(4, '0'))).catch(() => {});
    }, []);

    async function handleCommit() {
        if (!commitMessage.trim()) { setError('Commit message is required.'); return; }
        const name  = authorName.trim()  || DEFAULT_COMMIT_AUTHOR_NAME;
        const email = authorEmail.trim() || DEFAULT_COMMIT_AUTHOR_EMAIL;
        if (!email.includes('@')) { setError('Author email must contain @.'); return; }
        setError(null);
        setIsCommitting(true);
        try {
            const result = await gitCommitWithAuthor(commitMessage.trim(), createTag ? serial : undefined, name, email);
            statusContext.postInfo(`Committed ${result.shortHash}${createTag ? ` (release/${serial})` : ''}`);
            setCommitMessage('');
            const ns = await gitNextSerial();
            setSerial(String(ns.nextSerial).padStart(4, '0'));
        } catch (e: any) {
            const msg = e?.message || String(e);
            setError(msg);
            statusContext.postError(`Commit failed: ${msg}`);
        } finally { setIsCommitting(false); }
    }

    return (
        <div>
            <PageHeader title="Commit" subtitle="Commit data/prod.db to git and push" />
            {error && <ErrorBanner msg={error} onClose={() => setError(null)} />}
            <SectionCard title="New Commit" subtitle="Run Save to GIT (Sync tab) first if you have unsaved changes.">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 560 }}>
                    <Field label="Commit message">
                        <textarea
                            rows={2} value={commitMessage} disabled={isCommitting}
                            onChange={e => setCommitMessage(e.target.value)}
                            placeholder="Describe the changes…"
                            style={{ ...inputStyle, resize: 'vertical', minHeight: 64 }}
                        />
                    </Field>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <Field label="Author name">
                            <input type="text" value={authorName} disabled={isCommitting}
                                onChange={e => setAuthorName(e.target.value)}
                                placeholder={DEFAULT_COMMIT_AUTHOR_NAME} style={inputStyle} />
                        </Field>
                        <Field label="Author email">
                            <input type="email" value={authorEmail} disabled={isCommitting}
                                onChange={e => setAuthorEmail(e.target.value)}
                                placeholder={DEFAULT_COMMIT_AUTHOR_EMAIL} style={inputStyle} />
                        </Field>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: isCommitting ? 'not-allowed' : 'pointer', color: T.textMuted, fontSize: '0.82rem' }}>
                        <input type="checkbox" checked={createTag} disabled={isCommitting} onChange={e => setCreateTag(e.target.checked)} />
                        Create release tag
                    </label>
                    {createTag && (
                        <Field label="Serial number" hint={`Will create tag release/${serial}`} style={{ maxWidth: 180 }}>
                            <input type="text" value={serial} disabled={isCommitting}
                                onChange={e => setSerial(e.target.value)}
                                placeholder="e.g. 0042" style={inputStyle} />
                        </Field>
                    )}
                    <div>
                        <Btn variant="solid" color={T.action} disabled={isCommitting || !commitMessage.trim()} onClick={handleCommit}>
                            {isCommitting ? <><Spinner size="sm" />Committing…</> : 'Commit & Push'}
                        </Btn>
                    </div>
                </div>
            </SectionCard>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sync tab
// ---------------------------------------------------------------------------

function SyncTab() {
    const statusContext = useContext(StatusContext);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving,  setIsSaving]  = useState(false);
    const [error,     setError]     = useState<string | null>(null);

    async function handleLoad() {
        setError(null);
        setIsLoading(true);
        try {
            const f = await importGit();
            statusContext.postInfo(`Loaded database from git: ${f}`);
        } catch (e: any) {
            const msg = e?.message || String(e);
            setError(msg);
            statusContext.postError(`Load from GIT failed: ${msg}`);
        } finally { setIsLoading(false); }
    }

    async function handleSave() {
        setError(null);
        setIsSaving(true);
        try {
            const f = await exportGit();
            statusContext.postInfo(`Saved database to git: ${f}`);
        } catch (e: any) {
            const msg = e?.message || String(e);
            setError(msg);
            statusContext.postError(`Save to GIT failed: ${msg}`);
        } finally { setIsSaving(false); }
    }

    const busy = isLoading || isSaving;

    return (
        <div>
            <PageHeader title="Sync" subtitle="Load or save data/prod.db between the active database and git working tree" />
            {error && <ErrorBanner msg={error} onClose={() => setError(null)} />}
            <SectionCard title="Git Sync">
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <Btn variant="ghost" color={T.warning} disabled={busy} onClick={handleLoad}>
                        {isLoading ? <><Spinner size="sm" />Loading…</> : 'Load from GIT'}
                    </Btn>
                    <span style={{ color: T.textFaint, fontSize: '0.75rem' }}>⚠ Will close the current database — save if needed.</span>
                </div>
                <div style={{ marginTop: 10 }}>
                    <Btn variant="solid" color={T.action} disabled={busy} onClick={handleSave}>
                        {isSaving ? <><Spinner size="sm" />Saving…</> : 'Save to GIT'}
                    </Btn>
                </div>
            </SectionCard>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Publish tab
// ---------------------------------------------------------------------------

function PublishTab() {
    const statusContext = useContext(StatusContext);
    const wsContext = useContext(WsContext);

    const [state,             setState]             = useState<GitPublishStateResult | null>(null);
    const [isLoading,         setIsLoading]         = useState(false);
    const [isPushing,         setIsPushing]         = useState(false);
    const [pushResult,        setPushResult]        = useState<PushGitToS3Result | null>(null);
    const [error,             setError]             = useState<string | null>(null);
    const [pushProgressId,    setPushProgressId]    = useState<string | null>(null);
    const [pushProgressNow,   setPushProgressNow]   = useState(0);
    const [pushProgressLabel, setPushProgressLabel] = useState('');
    const [pushBytesXfr,      setPushBytesXfr]      = useState(0);
    const [pushTotalBytes,    setPushTotalBytes]     = useState<number | null>(null);
    const [pushOps,           setPushOps]           = useState<string[]>([]);
    const [forcePush,         setForcePush]         = useState(false);

    const loadState = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        setPushResult(null);
        try { setState(await getGitPublishState()); }
        catch (e: any) {
            const msg = e?.message || String(e);
            setError(msg);
            statusContext.postError(`Failed to load publish state: ${msg}`);
        } finally { setIsLoading(false); }
    }, [statusContext]);

    useEffect(() => { loadState(); }, [loadState]);

    useEffect(() => {
        if (!wsContext.ws || !pushProgressId) return;
        const handler = (msg: any) => {
            if (msg?.progressId !== pushProgressId || msg?.op !== 'git-publish') return;
            if (typeof msg?.now === 'number') setPushProgressNow(Math.max(0, Math.min(100, msg.now)));
            if (typeof msg?.bytesTransferred === 'number') setPushBytesXfr(Math.max(0, msg.bytesTransferred));
            if (typeof msg?.totalBytes === 'number' && msg.totalBytes > 0) setPushTotalBytes(msg.totalBytes);
            else if (msg?.phase === 'start' || !msg?.totalBytes) setPushTotalBytes(null);
            const label = msg?.label || 'Pushing to S3...';
            setPushProgressLabel(label);
            setPushOps(prev => {
                if (prev[prev.length - 1] === label) return prev;
                return [...prev, label].slice(-8);
            });
        };
        wsContext.ws.on('fileTransferProgress', handler);
        return () => { wsContext.ws.off('fileTransferProgress', handler); };
    }, [wsContext.ws, pushProgressId]);

    async function handlePush() {
        setError(null);
        setPushResult(null);
        setIsPushing(true);
        const pid = crypto.randomUUID();
        setPushProgressId(pid);
        setPushProgressNow(0);
        setPushProgressLabel('Preparing push to S3...');
        setPushBytesXfr(0);
        setPushTotalBytes(null);
        setPushOps(['Preparing push to S3...']);
        try {
            const result = await pushGitToS3(pid, forcePush);
            setPushResult(result);
            if (result.errors.length === 0) {
                statusContext.postInfo(`Push complete: ${result.processed} processed, ${result.skipped} skipped.`);
            } else {
                statusContext.postError(`Push finished with ${result.errors.length} error(s).`);
            }
            await loadState();
        } catch (e: any) {
            const msg = e?.message || String(e);
            setError(msg);
            statusContext.postError(`Push to S3 failed: ${msg}`);
        } finally {
            setPushProgressNow(100);
            setIsPushing(false);
        }
    }

    function closePushStatus() {
        if (isPushing) return;
        setPushProgressId(null);
        setPushProgressNow(0);
        setPushProgressLabel('');
        setPushBytesXfr(0);
        setPushTotalBytes(null);
        setPushOps([]);
    }

    const busy = isLoading || isPushing;
    const unpushedCount = state
        ? state.commits.filter(c => c.serial !== undefined ? (!c.inPublic || !c.inDev) : !c.inDev).length
        : 0;
    const showPushPanel = isPushing || Boolean(pushResult) || pushOps.length > 0;

    return (
        <div>
            <PageHeader title="Publish" subtitle="Push git history to S3 for distribution" />
            {error && <ErrorBanner msg={error} onClose={() => setError(null)} />}

            <SectionCard title="Push to S3" action={<Btn variant="ghost" color={T.textMuted} disabled={busy} onClick={loadState}>{isLoading ? <Spinner size="sm" /> : 'Refresh'}</Btn>}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: pushResult ? 14 : 0 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: busy ? 'not-allowed' : 'pointer', color: T.textMuted, fontSize: '0.82rem' }}>
                        <input type="checkbox" checked={forcePush} disabled={busy} onChange={e => setForcePush(e.target.checked)} />
                        Force
                    </label>
                    <Btn variant="solid" color={T.action} disabled={busy || (!forcePush && unpushedCount === 0)} onClick={handlePush}>
                        {isPushing ? <><Spinner size="sm" />Pushing to S3…</> : `Push to S3${!forcePush && unpushedCount > 0 ? ` (${unpushedCount} pending)` : ''}`}
                    </Btn>
                </div>
                {pushResult && (
                    <div style={{ marginTop: 12, padding: '8px 12px', background: pushResult.errors.length > 0 ? `${T.warning}18` : `${T.success}18`, border: `1px solid ${pushResult.errors.length > 0 ? T.warning : T.success}44`, borderRadius: 5, fontSize: '0.8rem', color: pushResult.errors.length > 0 ? T.warning : T.success }}>
                        Push complete: <strong>{pushResult.processed}</strong> processed, <strong>{pushResult.skipped}</strong> skipped.
                        {pushResult.errors.length > 0 && <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>{pushResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
                    </div>
                )}
            </SectionCard>

            {/* Inline push progress panel */}
            {showPushPanel && (
                <SectionCard title="Push Status" action={!isPushing ? <Btn variant="ghost" color={T.textMuted} onClick={closePushStatus}>Dismiss</Btn> : undefined}>
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ color: T.textMuted, fontSize: '0.8rem', marginBottom: 8 }}>{pushProgressLabel || 'Waiting...'}</div>
                        <MiniProgressBar now={pushProgressNow} animated={isPushing} />
                        <div style={{ color: T.textFaint, fontSize: '0.75rem', marginTop: 6 }}>
                            {pushTotalBytes ? `${fmtBytes(pushBytesXfr)} / ${fmtBytes(pushTotalBytes)}` : isPushing ? 'Working...' : 'Idle'}
                        </div>
                    </div>
                    {pushOps.length > 0 && (
                        <div style={{ maxHeight: '10rem', overflowY: 'auto', fontSize: '0.78rem', color: T.textMuted }}>
                            {pushOps.map((e, i) => <div key={`${i}-${e}`} style={{ marginBottom: 2 }}>{e}</div>)}
                        </div>
                    )}
                </SectionCard>
            )}

            <SectionCard title="Public S3 Files" subtitle="public/ci3_1_*.fbot">
                {isLoading ? <div style={{ color: T.textMuted }}><Spinner size="sm" className="me-2" />Loading…</div>
                    : !state || state.publicFiles.length === 0 ? <div style={{ color: T.textMuted }}>No public files found.</div>
                    : <DataTable
                        headers={['Name', 'Size', 'Modified']}
                        rows={state.publicFiles.map(f => [
                            <code style={{ color: T.action, fontSize: '0.78rem' }}>{f.name}</code>,
                            <span style={{ color: T.textMuted }}>{fmtBytes(f.size)}</span>,
                            <span style={{ color: T.textMuted }}>{fmtDate(f.modified)}</span>,
                        ])}
                    />}
            </SectionCard>

            <SectionCard title="Dev S3 Files" subtitle="library/dev/ser_*.fbot">
                {isLoading ? <div style={{ color: T.textMuted }}><Spinner size="sm" className="me-2" />Loading…</div>
                    : !state || state.devFiles.length === 0 ? <div style={{ color: T.textMuted }}>No dev files found.</div>
                    : <DataTable
                        headers={['Name', 'Size', 'Modified']}
                        rows={state.devFiles.map(f => [
                            <code style={{ color: T.action, fontSize: '0.78rem' }}>{f.name}</code>,
                            <span style={{ color: T.textMuted }}>{fmtBytes(f.size)}</span>,
                            <span style={{ color: T.textMuted }}>{fmtDate(f.modified)}</span>,
                        ])}
                    />}
            </SectionCard>

            <SectionCard title="Git Commit History — data/prod.db">
                {isLoading ? <div style={{ color: T.textMuted }}><Spinner size="sm" className="me-2" />Loading…</div>
                    : !state || state.commits.length === 0 ? <div style={{ color: T.textMuted }}>No commits found.</div>
                    : <DataTable
                        headers={['Hash', 'Date', 'Message', 'Serial', 'Public', 'Dev']}
                        rows={state.commits.map(c => [
                            <code style={{ color: T.action, fontSize: '0.78rem' }}>{c.shortHash}</code>,
                            <span style={{ color: T.textMuted, whiteSpace: 'nowrap' }}>{safeDateYmd(c.date)}</span>,
                            <span>{c.message}</span>,
                            c.serial ? <Chip label={String(c.serial)} color={T.success} /> : <span style={{ color: T.textFaint }}>—</span>,
                            c.serial ? <span style={{ color: c.inPublic ? T.success : T.textFaint }}>{c.inPublic ? '✓' : '–'}</span> : <span style={{ color: T.textFaint }}>n/a</span>,
                            <span style={{ color: c.inDev ? T.success : T.textFaint }}>{c.inDev ? '✓' : '–'}</span>,
                        ])}
                    />}
            </SectionCard>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Bundle tab (fbot build)
// ---------------------------------------------------------------------------

type BuildStep = {
    key: FbotBuildAction;
    title: string;
    group: 'bundle' | 'deploy';
    needsTarget: boolean;
    note: string;
};

const BUILD_STEPS: BuildStep[] = [
    { key: 'bump_release',      title: '1. Increment Release', group: 'bundle', needsTarget: false, note: 'Runs npm bump_release to increment the release number.' },
    { key: 'build_linux_bundle', title: '2. Build Linux Bundle', group: 'bundle', needsTarget: false, note: 'Creates fbot-<version>-linux-x64.tar.gz in infrastructure/out/deploy.' },
    { key: 'upload_release',    title: '3. Upload Release',    group: 'deploy', needsTarget: false, note: 'Uploads the build to the firmware update system.' },
    { key: 'first_install',     title: '4. First Install',     group: 'deploy', needsTarget: true,  note: 'Installs the bundle on a fresh target host.' },
    { key: 'push_release',      title: '5. Push Release',      group: 'deploy', needsTarget: true,  note: 'Stages release update and restarts remote service.' },
];

const DEFAULT_BUILD_OPTIONS: FbotBuildCommonOptions = {
    target: 'demo', host: '', version: '', region: 'us-west-2', keyPath: '',
    createTag: true,
    bundleBuildMode: 'native', bundleBuildImage: 'fbot-bundle-build:rocky9',
    s3Bucket: '', s3LinuxPrefix: '', sshUser: 'rocky', suppressWeakCryptoWarning: false,
};

function WorkflowStepCard({ step, note, isRunning, isAnyRunning, onRun }: {
    step: BuildStep; note: string; isRunning: boolean; isAnyRunning: boolean; onRun: () => void;
}) {
    const stepNum = step.title.match(/^\d+/)?.[0] || '';
    const titleLabel = step.title.replace(/^\d+\. /, '');
    return (
        <div style={{
            background: T.card, borderRadius: 7, padding: '14px 16px',
            border: `1px solid ${isRunning ? T.action : T.border}`,
            boxShadow: isRunning ? `0 0 0 1px ${T.action}33` : 'none',
            flex: '1 1 190px', minWidth: 190, maxWidth: 260,
            display: 'flex', flexDirection: 'column', gap: 10,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                    width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                    background: isRunning ? T.action : T.cardEl,
                    color: isRunning ? '#fff' : T.textMuted,
                    fontSize: '0.75rem', fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{stepNum}</span>
                <span style={{ color: T.textPrimary, fontWeight: 600, fontSize: '0.82rem' }}>{titleLabel}</span>
                {isRunning && <span style={{ marginLeft: 'auto', color: T.action, fontSize: '0.75rem' }}>Running…</span>}
            </div>
            <div style={{ color: T.textMuted, fontSize: '0.75rem', lineHeight: 1.45, flexGrow: 1 }}>{note}</div>
            <Btn variant="ghost" color={isRunning ? T.action : T.action} disabled={isAnyRunning} onClick={onRun}>
                {isRunning ? <><Spinner size="sm" />Executing…</> : 'Execute'}
            </Btn>
        </div>
    );
}

function StepGroup({ steps, label, runningAction, computeNote, onRun }: {
    steps: BuildStep[]; label: string; runningAction: FbotBuildAction | null;
    computeNote: (s: BuildStep) => string; onRun: (s: BuildStep) => void;
}) {
    return (
        <div style={{ marginBottom: 28 }}>
            <div style={{ color: T.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>{label}</div>
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, flexWrap: 'wrap' }}>
                {steps.map((step, idx) => (
                    <div key={step.key} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                        <WorkflowStepCard
                            step={step}
                            note={computeNote(step)}
                            isRunning={runningAction === step.key}
                            isAnyRunning={runningAction !== null}
                            onRun={() => onRun(step)}
                        />
                        {idx < steps.length - 1 && (
                            <div style={{ color: T.textFaint, fontSize: '1.1rem', padding: '0 8px', flexShrink: 0 }}>→</div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function ActionTile({ title, note, isRunning, disabled, onRun }: {
    title: string;
    note: string;
    isRunning: boolean;
    disabled: boolean;
    onRun: () => void;
}) {
    return (
        <div style={{
            background: T.cardEl,
            borderRadius: 7,
            border: `1px solid ${isRunning ? T.action : T.border}`,
            padding: '12px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
        }}>
            <div style={{ color: T.textPrimary, fontWeight: 600, fontSize: '0.83rem' }}>{title}</div>
            <div style={{ color: T.textMuted, fontSize: '0.75rem', lineHeight: 1.45, minHeight: 34 }}>{note}</div>
            <Btn variant="ghost" color={T.action} disabled={disabled} onClick={onRun}>
                {isRunning ? <><Spinner size="sm" />Executing…</> : 'Execute'}
            </Btn>
        </div>
    );
}

function FbotBuildTab() {
    const statusContext = useContext(StatusContext);
    const metadata = useContext<any>(MetadataContext);
    const [opts,          setOpts]          = useState<FbotBuildCommonOptions>(DEFAULT_BUILD_OPTIONS);
    const [targetChoices, setTargetChoices] = useState<string[]>([]);
    const [bundleInfo,    setBundleInfo]    = useState<BundleWorkflowInfo | null>(null);
    const [runningAction, setRunningAction] = useState<FbotBuildAction | null>(null);
    const [result,        setResult]        = useState<string>('');
    const [error,         setError]         = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        terraformListSiteNames().then(names => { if (mounted) setTargetChoices(Array.isArray(names) ? names : []); }).catch(() => { if (mounted) setTargetChoices([]); });
        return () => { mounted = false; };
    }, []);

    const refreshBundleInfo = useCallback(async () => {
        try { setBundleInfo(await getBundleWorkflowInfo()); } catch { setBundleInfo(null); }
    }, []);

    useEffect(() => { refreshBundleInfo(); }, [refreshBundleInfo]);

    function setOpt<K extends keyof FbotBuildCommonOptions>(key: K, value: FbotBuildCommonOptions[K]) {
        setOpts(prev => ({ ...prev, [key]: value }));
    }

    function computeStepNote(step: BuildStep): string {
        const effectiveAutoVersion = String(bundleInfo?.packageVersion || metadata?.appVersion || '').trim();
        const version = (opts.version || '').trim() || effectiveAutoVersion || 'auto';
        const buildBundleName = `fbot-${version}-linux-x64.tar.gz`;
        const deployBundleName = String(bundleInfo?.latestBundleName || '').trim();
        switch (step.key) {
            case 'bump_release':      return 'Requires clean git tree, bumps version, commits as "release <version>", and optionally creates a matching git tag.';
            case 'build_linux_bundle': return `Creates ${buildBundleName} in infrastructure/out/deploy.`;
            case 'upload_release':    return `Uploads ${buildBundleName} to the firmware update system.`;
            case 'first_install':     return deployBundleName ? `Installs ${deployBundleName} on a fresh target host.` : 'No local bundle found yet in infrastructure/out/deploy.';
            case 'push_release':      return deployBundleName ? `Stages ${deployBundleName} for release and restarts remote service.` : 'No local bundle found yet in infrastructure/out/deploy.';
            default:                  return step.note;
        }
    }

    function validateForStep(step: BuildStep): string | null {
        const hasHost = Boolean((opts.host || '').trim());
        const hasTarget = Boolean((opts.target || '').trim());
        if (hasHost && hasTarget) return 'Use either Host or Target, not both.';
        if (step.needsTarget && !hasHost && !hasTarget) return 'This step requires either Host or Target.';
        return null;
    }

    async function runStep(step: BuildStep, optOverrides?: Partial<FbotBuildCommonOptions>) {
        const v = validateForStep(step);
        if (v) { setError(v); return; }
        setError(null);
        setResult('');
        setRunningAction(step.key);
        try {
            const rc = await runFbotBuildAction(step.key, { ...opts, ...(optOverrides || {}) });
            const output = [rc.stdout, rc.stderr].filter(Boolean).join('\n').trim();
            setResult(output || `${step.key} completed successfully.`);
            statusContext.postInfo(`fbot build: ${step.key} completed.`);
            await refreshBundleInfo();
        } catch (e: any) {
            const msg = e?.message || String(e);
            setError(msg);
            statusContext.postError(`fbot build failed (${step.key}): ${String(msg).split('\n')[0]}`);
        } finally { setRunningAction(null); }
    }

    const customTargetValue = '__custom_target__';
    const trimmedTarget = (opts.target || '').trim();
    const targetInChoices = trimmedTarget.length > 0 && targetChoices.includes(trimmedTarget);
    const targetSelectValue = targetInChoices ? trimmedTarget : customTargetValue;

    const bundleSteps = BUILD_STEPS.filter(s => s.group === 'bundle');
    const deploySteps = BUILD_STEPS.filter(s => s.group === 'deploy');
    const buildLinuxStep = bundleSteps.find(s => s.key === 'build_linux_bundle');
    const uploadReleaseStep = deploySteps.find(s => s.key === 'upload_release');
    const firstInstallStep = deploySteps.find(s => s.key === 'first_install');
    const pushReleaseStep = deploySteps.find(s => s.key === 'push_release');

    return (
        <div>
            <PageHeader
                title="Bundle"
                subtitle="Bundle and deploy Linux release artifacts"
                chips={[opts.region || 'us-west-2', opts.bundleBuildMode || 'native']}
            />
            {error && <ErrorBanner msg={error} onClose={() => setError(null)} />}

            <SectionCard title="Bundle" subtitle="Build options and release artifact actions">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                    <Field label="Version" hint="Defaults to package.json">
                        <input type="text" value={opts.version || ''} onChange={e => setOpt('version', e.target.value)} placeholder="auto" style={inputStyle} />
                    </Field>
                    <Field label="Region">
                        <input type="text" value={opts.region || ''} onChange={e => setOpt('region', e.target.value)} placeholder="us-west-2" style={inputStyle} />
                    </Field>
                    <Field label="Build Mode">
                        <select value={opts.bundleBuildMode || 'native'} onChange={e => setOpt('bundleBuildMode', e.target.value as 'container' | 'native')} style={{ ...inputStyle, appearance: 'auto' }}>
                            <option value="container">container</option>
                            <option value="native">native</option>
                        </select>
                    </Field>
                    <Field label="Build Image" style={{ gridColumn: 'span 2' }}>
                        <input type="text" value={opts.bundleBuildImage || ''} onChange={e => setOpt('bundleBuildImage', e.target.value)} placeholder="fbot-bundle-build:rocky9" style={inputStyle} />
                    </Field>
                    <Field label="S3 Bucket">
                        <input type="text" value={opts.s3Bucket || ''} onChange={e => setOpt('s3Bucket', e.target.value)} placeholder="ipivot-fbot" style={inputStyle} />
                    </Field>
                    <Field label="S3 Linux Prefix" style={{ gridColumn: 'span 2' }}>
                        <input type="text" value={opts.s3LinuxPrefix || ''} onChange={e => setOpt('s3LinuxPrefix', e.target.value)} placeholder="fbot/releases/linux-prod" style={inputStyle} />
                    </Field>
                </div>
                <div style={{ marginTop: 14, color: T.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Actions</div>
                <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12 }}>
                    <div style={{
                        background: T.cardEl,
                        borderRadius: 7,
                        border: `1px solid ${runningAction === 'bump_release' ? T.action : T.border}`,
                        padding: '12px 14px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                    }}>
                        <div style={{ color: T.textPrimary, fontWeight: 600, fontSize: '0.83rem' }}>Increment Release</div>
                        <div style={{ color: T.textMuted, fontSize: '0.75rem', lineHeight: 1.45 }}>
                            Increment package version and commit/tag. Tree must be clean.
                        </div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: T.textMuted, fontSize: '0.78rem', cursor: 'pointer' }}>
                            <input type="checkbox" checked={opts.createTag !== false} onChange={e => setOpt('createTag', e.target.checked)} />
                            Create git tag
                        </label>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Btn variant="ghost" color={T.action} disabled={runningAction !== null} onClick={() => void runStep({ key: 'bump_release', title: 'Increment Release', group: 'bundle', needsTarget: false, note: '' }, { bumpReleaseMode: 'minor' })}>
                                Minor
                            </Btn>
                            <Btn variant="ghost" color={T.action} disabled={runningAction !== null} onClick={() => void runStep({ key: 'bump_release', title: 'Increment Release', group: 'bundle', needsTarget: false, note: '' }, { bumpReleaseMode: 'patch' })}>
                                Patch
                            </Btn>
                            <Btn variant="ghost" color={T.action} disabled={runningAction !== null} onClick={() => void runStep({ key: 'bump_release', title: 'Increment Release', group: 'bundle', needsTarget: false, note: '' }, { bumpReleaseMode: 'auto' })}>
                                Patch or Alpha/Beta
                            </Btn>
                        </div>
                    </div>
                    {buildLinuxStep && (
                        <ActionTile
                            title="Build Linux Bundle"
                            note={computeStepNote(buildLinuxStep)}
                            isRunning={runningAction === buildLinuxStep.key}
                            disabled={runningAction !== null}
                            onRun={() => void runStep(buildLinuxStep)}
                        />
                    )}
                </div>
            </SectionCard>

            <SectionCard title="Deploy" subtitle="Target options and deployment actions (either action may be run)">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                    <Field label="Target (site_name)">
                        <select value={targetSelectValue} onChange={e => {
                            const next = e.target.value;
                            if (next === customTargetValue) { if (targetInChoices) setOpt('target', ''); return; }
                            setOpt('target', next);
                        }} style={{ ...inputStyle, appearance: 'auto' }}>
                            <option value={customTargetValue}>Custom…</option>
                            {targetChoices.map(name => <option key={name} value={name}>{name}</option>)}
                        </select>
                        {targetSelectValue === customTargetValue && (
                            <input type="text" style={{ ...inputStyle, marginTop: 6 }} value={opts.target || ''} onChange={e => setOpt('target', e.target.value)} placeholder="Enter target" />
                        )}
                    </Field>
                    <Field label="Host" hint="Optional — use instead of Target">
                        <input type="text" value={opts.host || ''} onChange={e => setOpt('host', e.target.value)} placeholder="rocky@1.2.3.4" style={inputStyle} />
                    </Field>
                    <Field label="Version">
                        <input type="text" value={opts.version || ''} onChange={e => setOpt('version', e.target.value)} placeholder="auto" style={inputStyle} />
                    </Field>
                    <Field label="Region">
                        <input type="text" value={opts.region || ''} onChange={e => setOpt('region', e.target.value)} placeholder="us-west-2" style={inputStyle} />
                    </Field>
                    <Field label="SSH Key Path" style={{ gridColumn: 'span 2' }}>
                        <input type="text" value={opts.keyPath || ''} onChange={e => setOpt('keyPath', e.target.value)} placeholder="~/.ssh/evipivot.pem" style={inputStyle} />
                    </Field>
                    <Field label="SSH User">
                        <input type="text" value={opts.sshUser || ''} onChange={e => setOpt('sshUser', e.target.value)} placeholder="rocky" style={inputStyle} />
                    </Field>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gridColumn: 'span 2' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: T.textMuted, fontSize: '0.8rem', cursor: 'pointer' }}>
                            <input type="checkbox" checked={opts.suppressWeakCryptoWarning === true} onChange={e => setOpt('suppressWeakCryptoWarning', e.target.checked)} />
                            Suppress SSH weak-crypto warning
                        </label>
                    </div>
                </div>
                <div style={{ marginTop: 14, color: T.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Actions</div>
                <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 12 }}>
                    {uploadReleaseStep && (
                        <ActionTile
                            title="Upload Release"
                            note={computeStepNote(uploadReleaseStep)}
                            isRunning={runningAction === uploadReleaseStep.key}
                            disabled={runningAction !== null}
                            onRun={() => void runStep(uploadReleaseStep)}
                        />
                    )}
                    {firstInstallStep && (
                        <ActionTile
                            title="First Install"
                            note={computeStepNote(firstInstallStep)}
                            isRunning={runningAction === firstInstallStep.key}
                            disabled={runningAction !== null}
                            onRun={() => void runStep(firstInstallStep)}
                        />
                    )}
                    {pushReleaseStep && (
                        <ActionTile
                            title="Push Release"
                            note={computeStepNote(pushReleaseStep)}
                            isRunning={runningAction === pushReleaseStep.key}
                            disabled={runningAction !== null}
                            onRun={() => void runStep(pushReleaseStep)}
                        />
                    )}
                </div>
            </SectionCard>

            {result && (
                <SectionCard title="Last Run Output">
                    <pre style={{ margin: 0, padding: '10px 14px', background: '#0a0f1a', color: '#c9d7f0', fontSize: '0.78rem', borderRadius: 5, maxHeight: '18rem', overflowY: 'auto', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                        {result}
                    </pre>
                </SectionCard>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Fingerprint workflow tab
// ---------------------------------------------------------------------------

type FpStepKey = 'load_git' | 'load_merge' | 'view_merge' | 'bump_serial' | 'save_git' | 'commit_git' | 'file_publish' | 'update_progress' | 'push_s3';
type FpStepStatus = 'pending' | 'running' | 'done' | 'error';

interface FpStep {
    key: FpStepKey;
    num: number;
    label: string;
    desc: string;
}

const LS_WORKFLOW_KEY = 'fbot:fpWorkflow';
function loadWorkflowState(): Record<string, any> {
    try { return JSON.parse(localStorage.getItem(LS_WORKFLOW_KEY) || '{}'); } catch { return {}; }
}

// Extract YYMMDDHHMM sort key from filenames like "neon_041626_1546.merge"
function mergeFileSortKey(name: string): string {
    const m = name.match(/_(\d{2})(\d{2})(\d{2})_(\d{4})(?:\.\w+)?$/);
    if (!m) return name;
    const [, mm, dd, yy, hhmm] = m;
    return `${yy}${mm}${dd}${hhmm}`;
}

const FP_STEPS: FpStep[] = [
    { key: 'load_git',        num: 1,  label: 'Load from Git',      desc: 'Load current prod.db from git working tree.' },
    { key: 'load_merge',      num: 2,  label: 'Load Merge Entry',    desc: 'Load a merge queue entry into the apply table.' },
    { key: 'view_merge',      num: 3,  label: 'Review Merge',         desc: 'Open the merge review page, accept/reject entries, then Commit & Continue.' },
    { key: 'bump_serial',     num: 4,  label: 'Bump Serial',         desc: 'Get the next serial and write it to the database metadata.' },
    { key: 'save_git',        num: 6,  label: 'Save to Git',         desc: 'Write current prod.db back to the git tree.' },
    { key: 'commit_git',      num: 7,  label: 'Commit to Git',       desc: 'Create a git commit with the change description.' },
    { key: 'file_publish',    num: 8,  label: 'File / Publish',      desc: 'Push git commits to S3 Production and Dev directories.' },
    { key: 'update_progress', num: 9,  label: 'Update Progress',     desc: 'Recalculate progress statistics from git history.' },
    { key: 'push_s3',         num: 10, label: 'Push Progress to S3', desc: 'Publish updated progress statistics to S3.' },
];

const FLOW1_KEYS: FpStepKey[] = ['load_git', 'load_merge', 'view_merge'];
const FLOW2_KEYS: FpStepKey[] = ['bump_serial', 'save_git', 'commit_git', 'file_publish', 'update_progress', 'push_s3'];
const FP_STEP_ORDER: FpStepKey[] = [...FLOW1_KEYS, ...FLOW2_KEYS];

function FingerprintWorkflowTab() {
    const wsContext     = useContext(WsContext);
    const statusContext = useContext(StatusContext);
    const navigate      = useNavigate();
    const location      = useLocation();
    const [searchParams] = useSearchParams();

    const initStatuses = (): Record<FpStepKey, FpStepStatus> =>
        Object.fromEntries(FP_STEP_ORDER.map(k => [k, 'pending' as FpStepStatus])) as Record<FpStepKey, FpStepStatus>;
    const initLogs = (): Record<FpStepKey, string[]> =>
        Object.fromEntries(FP_STEP_ORDER.map(k => [k, [] as string[]])) as Record<FpStepKey, string[]>;

    const [statuses,     setStatuses]     = useState<Record<FpStepKey, FpStepStatus>>(() => {
        const saved = loadWorkflowState().statuses || {};
        return Object.fromEntries(FP_STEP_ORDER.map(k => [
            k, saved[k] === 'running' ? 'pending' : (saved[k] || 'pending'),
        ])) as Record<FpStepKey, FpStepStatus>;
    });
    const [stepSkips,    setStepSkips]    = useState<Partial<Record<FpStepKey, boolean>>>(
        () => loadWorkflowState().stepSkips || {}
    );
    const [stepLogs,     setStepLogs]     = useState<Record<FpStepKey, string[]>>(initLogs);
    const [mergeFile,    setMergeFile]    = useState<string>(() => searchParams.get('mergeFile') || loadWorkflowState().mergeFile || '');
    const [cloudQueue,   setCloudQueue]   = useState<boolean>(searchParams.get('cloud') !== 'false');
    const [queueEntries, setQueueEntries] = useState<MergeQueueEntry[]>([]);
    const [queueRefresh, setQueueRefresh] = useState<number>(0);
    const [commitMsg,    setCommitMsg]    = useState<string>('');
    const [nextSerial,   setNextSerial]   = useState<string>(() => loadWorkflowState().nextSerial || '');
    const [diffStats,    setDiffStats]    = useState<{ fpCount: number; catCount: number } | null>(null);
    const [runningStep,  setRunningStepState] = useState<FpStepKey | null>(null);
    const [runMode,      setRunMode]      = useState<null | 'flow1' | 'flow2'>(null);
    const [error,        setError]        = useState<string | null>(null);

    const runningStepRef = useRef<FpStepKey | null>(null);
    const commitMsgRef   = useRef<string>('');
    const nextSerialRef  = useRef<string>(loadWorkflowState().nextSerial || '');
    const stopReqRef     = useRef(false);

    function setRunningStep(k: FpStepKey | null) {
        runningStepRef.current = k;
        setRunningStepState(k);
    }

    function pushStepLog(key: FpStepKey, line: string) {
        setStepLogs(prev => ({ ...prev, [key]: [...prev[key], line].slice(-20) }));
    }

    useEffect(() => {
        if (!commitMsgRef.current) {
            const msg = loadWorkflowState().commitMsg || '';
            if (!msg) {
                try {
                    const saved = localStorage.getItem('fbot:lastMergeMetadata');
                    const info = saved ? JSON.parse(saved)?.info : '';
                    if (info) { commitMsgRef.current = info; setCommitMsg(info); }
                } catch {}
            } else {
                commitMsgRef.current = msg;
                setCommitMsg(msg);
            }
        }
    }, []);

    useEffect(() => {
        const primeCommitMsg = String((location.state as any)?.primeCommitMsg || '').trim();
        if (!primeCommitMsg) return;
        commitMsgRef.current = primeCommitMsg;
        setCommitMsg(primeCommitMsg);
    }, [location.state]);

    useEffect(() => {
        try {
            localStorage.setItem(LS_WORKFLOW_KEY, JSON.stringify({ statuses, mergeFile, nextSerial, commitMsg, stepSkips }));
        } catch {}
    }, [statuses, mergeFile, nextSerial, commitMsg, stepSkips]);

    useEffect(() => {
        if (!wsContext.ws) return;
        mergeImportQueue(cloudQueue)
            .then(rc => {
                const sorted = (rc.queue || []).slice().sort((a, b) => mergeFileSortKey(a.name).localeCompare(mergeFileSortKey(b.name)));
                setQueueEntries(sorted);
                setMergeFile(prev => {
                    if (prev && sorted.some(e => e.name === prev)) return prev;
                    return sorted[0]?.name || '';
                });
            })
            .catch(e => statusContext.postError(`Failed to load merge queue: ${e.message}`));
        refreshDiff();
    }, [cloudQueue, queueRefresh, wsContext.ws]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!wsContext.ws) return;
        const handler = (msg: any) => {
            const cur = runningStepRef.current;
            if (!cur) return;
            const text: string = typeof msg === 'string' ? msg.trim() : typeof msg === 'object' ? JSON.stringify(msg) : String(msg);
            setStepLogs(prev => ({ ...prev, [cur]: [...prev[cur], text].slice(-20) }));
        };
        wsContext.ws.on('log', handler);
        return () => { wsContext.ws.off('log', handler); };
    }, [wsContext.ws]);

    function resetAll() {
        setStatuses(initStatuses());
        setStepLogs(initLogs());
        setRunningStep(null);
        stopReqRef.current = false;
        setRunMode(null);
        setNextSerial('');
        commitMsgRef.current = '';
        setCommitMsg('');
        setStepSkips({});
        setError(null);
        try { localStorage.removeItem(LS_WORKFLOW_KEY); } catch {}
    }

    const refreshDiff = useCallback(async () => {
        try {
            const d = await getDiff();
            setDiffStats({ fpCount: d.fingerprints ?? 0, catCount: d.categories ?? 0 });
        } catch { setDiffStats(null); }
    }, []);

    async function execStep(key: FpStepKey): Promise<void> {
        switch (key) {
            case 'load_git':
                await importGit();
                break;
            case 'view_merge': {
                navigate('/data/merge-op');
                break;
            }
            case 'load_merge': {
                const name = mergeFile.trim();
                if (!name) throw new Error('No merge file selected — refresh the queue and select an entry.');
                const found = queueEntries.find(e => e.name === name);
                const info = found?.metadata?.info || '';
                if (info && !commitMsgRef.current) {
                    commitMsgRef.current = info;
                    setCommitMsg(info);
                }
                await mergeLoadFile(cloudQueue, name, true);
                break;
            }
            case 'bump_serial': {
                const [ns, meta] = await Promise.all([gitNextSerial(), getMetadata()]);
                const serial = String(ns.nextSerial).padStart(4, '0');
                nextSerialRef.current = serial;
                setNextSerial(serial);
                await setMetadata(meta.info || '', serial, meta.title, meta.branch);
                break;
            }
            case 'save_git':
                await exportGit();
                break;
            case 'commit_git': {
                const msg = commitMsgRef.current.trim() || 'Merge queue commit';
                const m = msg.match(/^submitted by (\S+)/i);
                const authorName = m ? m[1] : undefined;
                const serial = nextSerialRef.current || nextSerial || undefined;
                await gitCommitWithAuthor(msg, serial, authorName || 'FBot', '');
                break;
            }
            case 'file_publish': {
                const pid = crypto.randomUUID();
                await pushGitToS3(pid, false);
                break;
            }
            case 'update_progress': {
                // Snapshot commits a few at a time so the step reports progress
                // and a stop request lands between batches; whatever is cached
                // by then does not have to be recomputed next run.
                const r = await updateProgressBatched({
                    onProgress: (done, todo) => pushStepLog('update_progress', `Snapshotting ${done} / ${todo} commits`),
                    shouldStop: () => stopReqRef.current,
                });
                if (r.remaining > 0) {
                    pushStepLog('update_progress', `Stopped with ${r.remaining} commits left — re-run to continue`);
                }
                if (r.errors > 0) {
                    pushStepLog('update_progress', `${r.errors} commit(s) failed and were cached as failed`);
                }
                break;
            }
            case 'push_s3':
                await publishProgressToS3();
                break;
        }
    }

    async function runOneStep(key: FpStepKey): Promise<boolean> {
        setError(null);
        setRunningStep(key);
        setStepLogs(prev => ({ ...prev, [key]: [] }));
        const idx = FP_STEP_ORDER.indexOf(key);
        setStatuses(prev => {
            const next = { ...prev, [key]: 'running' as FpStepStatus };
            FP_STEP_ORDER.forEach((k, i) => { if (i > idx) next[k] = 'pending'; });
            return next;
        });
        try {
            await execStep(key);
            setStatuses(prev => ({ ...prev, [key]: 'done' }));
            return true;
        } catch (e: any) {
            const msg = e?.message || String(e);
            setError(msg);
            setStatuses(prev => ({ ...prev, [key]: 'error' }));
            setStepLogs(prev => ({ ...prev, [key]: [...prev[key], `Error: ${msg}`].slice(-20) }));
            statusContext.postError(`FP Workflow (${key}): ${String(msg).split('\n')[0]}`);
            return false;
        } finally {
            setRunningStep(null);
        }
    }

    async function runFlow(keys: FpStepKey[], mode: 'flow1' | 'flow2') {
        setRunMode(mode);
        stopReqRef.current = false;
        for (const key of keys) {
            if (stopReqRef.current) break;
            if (isSkipped(key)) continue;
            const ok = await runOneStep(key);
            if (!ok) break;
        }
        setRunMode(null);
    }

    const isAnyRunning = runningStep !== null;

    const isSkipped = (key: FpStepKey) =>
        !!stepSkips[key] || (key === 'push_s3' && !!stepSkips['update_progress']);

    function renderStatus(key: FpStepKey) {
        const s = statuses[key];
        if (s === 'running') return <span style={{ color: T.action,   display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem' }}><Spinner style={{ width: 12, height: 12 }} />Running</span>;
        if (s === 'done')    return <span style={{ color: T.success,  fontWeight: 600, fontSize: '0.78rem' }}>✓ Done</span>;
        if (s === 'error')   return <span style={{ color: T.danger,   fontWeight: 600, fontSize: '0.78rem' }}>✗ Error</span>;
        return <span style={{ color: T.textFaint, fontSize: '0.78rem' }}>—</span>;
    }

    function renderParams(key: FpStepKey) {
        const logs = stepLogs[key];
        const logBlock = logs.length > 0 ? (
            <div style={{ marginTop: 6, background: '#0a0f1a', borderRadius: 4, padding: '4px 8px', maxHeight: '5rem', overflowY: 'auto' }}>
                {logs.slice(-8).map((line, i) => (
                    <div key={i} style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: T.textMuted, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{line}</div>
                ))}
            </div>
        ) : null;

        const skipCheckbox = (k: FpStepKey, label = 'Skip') => (
            <label style={{ fontSize: '0.75rem', display: 'flex', gap: 5, alignItems: 'center',
                            cursor: 'pointer', color: T.textMuted }}>
                <input type="checkbox" checked={!!stepSkips[k]}
                       onChange={() => setStepSkips(p => ({ ...p, [k]: !p[k] }))}
                       disabled={isAnyRunning} />
                {label}
            </label>
        );

        if (key === 'load_git') return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {skipCheckbox('load_git')}
                {logBlock}
            </div>
        );

        if (key === 'load_merge') return (
            <div>
                <select
                    value={mergeFile}
                    onChange={e => setMergeFile(e.target.value)}
                    disabled={isAnyRunning}
                    style={{ ...inputStyle, appearance: 'auto', fontSize: '0.75rem', width: 'auto', maxWidth: 280 }}
                >
                    {queueEntries.length === 0 && <option value="">— queue empty —</option>}
                    {queueEntries.map(e => <option key={e.name} value={e.name}>{e.name}</option>)}
                </select>
                {logBlock}
            </div>
        );

        if (key === 'bump_serial' && nextSerial) return (
            <div>
                <span style={{ fontFamily: 'monospace', color: T.action, fontSize: '0.75rem' }}>{nextSerial}</span>
                {logBlock}
            </div>
        );

        if (key === 'commit_git') return (
            <div>
                <textarea
                    rows={2}
                    value={commitMsg}
                    onChange={e => { commitMsgRef.current = e.target.value; setCommitMsg(e.target.value); }}
                    disabled={isAnyRunning}
                    placeholder="Describe the change…"
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 44, fontSize: '0.75rem', width: 280 }}
                />
                {logBlock}
            </div>
        );

        if (key === 'file_publish') return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {skipCheckbox('file_publish')}
                {logBlock}
            </div>
        );

        if (key === 'update_progress') return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {skipCheckbox('update_progress', 'Skip (also skips Push to S3)')}
                {logBlock}
            </div>
        );

        return logBlock;
    }

    function renderFlowTable(steps: FpStep[]) {
        return (
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 7, marginBottom: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                            <th style={fpThStyle}>Status</th>
                            <th style={{ ...fpThStyle, width: 36 }}></th>
                            <th style={{ ...fpThStyle, width: 28 }}>#</th>
                            <th style={fpThStyle}>Op</th>
                            <th style={fpThStyle}>Description</th>
                            <th style={fpThStyle}>Parameters / Result</th>
                        </tr>
                    </thead>
                    <tbody>
                        {steps.map((step, i) => {
                            const s = statuses[step.key];
                            const rowBg = s === 'running' ? `${T.action}09` : s === 'error' ? `${T.danger}09` : s === 'done' ? `${T.success}07` : 'transparent';
                            return (
                                <tr key={step.key} style={{
                                    borderBottom: i < steps.length - 1 ? `1px solid ${T.border}33` : 'none',
                                    background: rowBg,
                                }}>
                                    <td style={fpTdStyle}>{renderStatus(step.key)}</td>
                                    <td style={{ ...fpTdStyle, padding: '8px' }}>
                                        <button
                                            disabled={isAnyRunning}
                                            onClick={() => runOneStep(step.key)}
                                            title={`Run ${step.label}`}
                                            style={{
                                                background: 'transparent',
                                                border: `1px solid ${T.border}`,
                                                borderRadius: 4, cursor: isAnyRunning ? 'not-allowed' : 'pointer',
                                                color: isAnyRunning ? T.textFaint : T.action,
                                                width: 26, height: 26,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '0.75rem', flexShrink: 0,
                                            }}
                                        >▶</button>
                                    </td>
                                    <td style={{ ...fpTdStyle, color: T.textFaint, fontFamily: 'monospace', fontSize: '0.75rem' }}>{step.num}</td>
                                    <td style={{ ...fpTdStyle, color: isSkipped(step.key) ? T.textFaint : T.textPrimary, fontWeight: 600, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{step.label}</td>
                                    <td style={{ ...fpTdStyle, color: T.textMuted, fontSize: '0.78rem', maxWidth: 240 }}>{step.desc}</td>
                                    <td style={{ ...fpTdStyle, minWidth: 160 }}>{renderParams(step.key)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    const flow1Steps = FP_STEPS.filter(s => (FLOW1_KEYS as string[]).includes(s.key));
    const flow2Steps = FP_STEPS.filter(s => (FLOW2_KEYS as string[]).includes(s.key));

    return (
        <div>
            <PageHeader title="Fingerprint Workflow" subtitle="Automated merge queue → commit → publish pipeline" />
            {error && <ErrorBanner msg={error} onClose={() => setError(null)} />}

            <SectionCard title="Controls">
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: T.textMuted, fontSize: '0.82rem' }}>
                        <input type="radio" checked={cloudQueue}  onChange={() => setCloudQueue(true)}  /> Cloud
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: T.textMuted, fontSize: '0.82rem' }}>
                        <input type="radio" checked={!cloudQueue} onChange={() => setCloudQueue(false)} /> Local
                    </label>
                    <Btn variant="ghost" color={T.textMuted} disabled={isAnyRunning} onClick={() => { setQueueRefresh(v => v + 1); refreshDiff(); }}>Refresh Queue</Btn>
                    <span style={{ color: T.textFaint, fontSize: '0.75rem' }}>|</span>
                    <Btn variant="solid" color={T.action} disabled={isAnyRunning} onClick={() => runFlow(FLOW1_KEYS, 'flow1')}>
                        {runMode === 'flow1' ? <><Spinner size="sm" />Running…</> : 'Start Review'}
                    </Btn>
                    <Btn variant="solid" color={T.action} disabled={isAnyRunning || (diffStats !== null && diffStats.fpCount === 0 && diffStats.catCount === 0)} onClick={() => runFlow(FLOW2_KEYS, 'flow2')}>
                        {runMode === 'flow2' ? <><Spinner size="sm" />Running…</> : 'Commit Changes'}
                    </Btn>
                    {isAnyRunning && <Btn variant="danger" onClick={() => { stopReqRef.current = true; }}>Stop</Btn>}
                    <Btn variant="ghost" color={T.textMuted} disabled={isAnyRunning} onClick={resetAll}>Reset</Btn>
                    {diffStats !== null && (
                        <span style={{ color: T.textFaint, fontSize: '0.78rem', marginLeft: 4 }}>
                            {diffStats.fpCount === 0 && diffStats.catCount === 0
                                ? 'No changes'
                                : [
                                    diffStats.fpCount > 0 ? `${diffStats.fpCount} FPs` : '',
                                    diffStats.catCount > 0 ? `${diffStats.catCount} cats` : '',
                                  ].filter(Boolean).join(', ')}
                        </span>
                    )}
                </div>
            </SectionCard>

            <SectionCard title="Start Review" subtitle="Load prod.db from git and import merge entry">
                {renderFlowTable(flow1Steps)}
            </SectionCard>

            <SectionCard title="Commit Changes" subtitle="Requires committed changes to the DB">
                {renderFlowTable(flow2Steps)}
            </SectionCard>

        </div>
    );
}

const fpThStyle: React.CSSProperties = {
    padding: '8px 12px', color: T.textMuted, fontSize: '0.75rem', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.06em',
    background: T.cardEl, textAlign: 'left', whiteSpace: 'nowrap',
};

const fpTdStyle: React.CSSProperties = {
    padding: '10px 12px', verticalAlign: 'top',
};


// ---------------------------------------------------------------------------
// DevPage shell with left-rail navigation
// ---------------------------------------------------------------------------

const TAB_META: Record<Tab, { label: string; icon: string; subtitle: string }> = {
    history:               { label: 'History',    icon: '◷', subtitle: 'Browse git commits' },
    compare:               { label: 'Compare',    icon: '≍', subtitle: 'Diff any two DBs' },
    commit:                { label: 'Commit',     icon: '◈', subtitle: 'Commit db to git' },
    sync:                  { label: 'Sync',       icon: '⇅', subtitle: 'Load/save prod.db' },
    publish:               { label: 'Publish',    icon: '⇪', subtitle: 'Push to S3' },
    'fbot-build':          { label: 'Bundle',     icon: '◧', subtitle: 'Build & deploy' },
    'fingerprint-workflow': { label: 'FP Workflow', icon: '⊕', subtitle: 'Merge queue → publish' },
};

function DevPage() {
    const navigate  = useNavigate();
    const location  = useLocation();
    const metadata  = useContext<any>(MetadataContext);

    const pathToTab = (p: string): Tab => {
        for (const t of TABS) { if (p.endsWith(`/${t}`)) return t; }
        return 'history';
    };

    const activeTab = pathToTab(location.pathname);

    useEffect(() => {
        if (location.pathname === '/dev' || location.pathname === '/dev/') {
            const last = (localStorage.getItem('fbot:dev:activeTab') as Tab) || 'history';
            navigate(`/dev/${last}`, { replace: true });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    function handleTabClick(tab: Tab) {
        localStorage.setItem('fbot:dev:activeTab', tab);
        navigate(`/dev/${tab}`);
    }

    if (!metadata?.devMode) {
        return (
            <div style={{ padding: 24, background: T.bg, minHeight: '100%' }}>
                <div style={{ background: `${T.warning}18`, border: `1px solid ${T.warning}44`, borderRadius: 6, padding: '12px 16px', color: T.warning, fontSize: '0.85rem' }}>
                    Dev tools are not active. Configure Dev mode in Settings to use this page.
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: T.bg }}>
            {/* Left rail */}
            <div style={{ width: 192, flexShrink: 0, background: T.rail, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', paddingTop: 20 }}>
                <div style={{ padding: '0 16px 16px', borderBottom: `1px solid ${T.border}33` }}>
                    <div style={{ color: T.textPrimary, fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.02em' }}>Developer</div>
                    <div style={{ color: T.textFaint, fontSize: '0.75rem', marginTop: 2 }}>Git &amp; release tools</div>
                </div>
                <nav style={{ paddingTop: 8 }}>
                    {TABS.map(tab => {
                        const meta = TAB_META[tab];
                        const active = activeTab === tab;
                        return (
                            <button
                                key={tab}
                                onClick={() => handleTabClick(tab)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    width: '100%', padding: '9px 16px',
                                    background: active ? `${T.action}14` : 'transparent',
                                    border: 'none',
                                    borderLeft: active ? `3px solid ${T.action}` : '3px solid transparent',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    transition: 'background 0.1s',
                                }}
                                onMouseEnter={e => { if (!active) (e.currentTarget as HTMLElement).style.background = T.cardEl; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = active ? `${T.action}14` : 'transparent'; }}
                            >
                                <span style={{ fontSize: '0.9rem', color: active ? T.action : T.textFaint, flexShrink: 0 }}>{meta.icon}</span>
                                <div>
                                    <div style={{ color: active ? T.textPrimary : T.textMuted, fontSize: '0.8rem', fontWeight: active ? 600 : 400 }}>{meta.label}</div>
                                    <div style={{ color: T.textFaint, fontSize: '0.75rem', marginTop: 1 }}>{meta.subtitle}</div>
                                </div>
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Main content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '28px 32px' }}>
                {activeTab === 'history'               && <HistoryTab />}
                {activeTab === 'compare'               && <CompareTab />}
                {activeTab === 'commit'                && <CommitTab />}
                {activeTab === 'sync'                  && <SyncTab />}
                {activeTab === 'publish'               && <PublishTab />}
                {activeTab === 'fbot-build'            && <FbotBuildTab />}
                {activeTab === 'fingerprint-workflow'  && <FingerprintWorkflowTab />}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtBytes(b: number): string {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(ms: number): string {
    return new Date(ms).toISOString().slice(0, 10);
}

export default DevPage;
